import React, { useState, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Grid,
  TextField,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Description,
  Schedule,
  Sync,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import EscritorioSelect from '../../selects/EscritorioSelect';
import { importService } from '../../../services/importService';

interface ImportSysupequipamentosModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedData {
  valid: boolean;
  duplicate: boolean;
  validationErrors?: string[];
  importStatus?: 'pending' | 'importing' | 'success' | 'error';
  importError?: string;
  [key: string]: any;
}

interface ImportStats {
  total: number;
  valid: number;
  invalid: number;
  duplicates: number;
}

interface ImportProgress {
  nome: string;
  status: 'processando' | 'sucesso' | 'erro';
  erro?: string;
}

const steps = [
  'Configuração da API',
  'Análise e Importação'
];

export const ImportSysupequipamentosModal: React.FC<ImportSysupequipamentosModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiUrl, setApiUrl] = useState('https://www.seusite.com.br/admin/addons/sysup/apiv2/listarEquipamentosFtth');
  const [apiToken, setApiToken] = useState('9f4c8db4c60a93f1a3a77a9dd11b2f5c49c634b9dcf32776f79c4fc343a3b07d');
  const [parsedData, setParsedData] = useState<ParsedData[]>([]);
  const [importStats, setImportStats] = useState<ImportStats>({
    total: 0,
    valid: 0,
    invalid: 0,
    duplicates: 0
  });
  // Estado de edição removido - interface simplificada
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ [key: number]: ImportProgress }>({});
  const [importCompleted, setImportCompleted] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedEscritorio, setSelectedEscritorio] = useState<number | null>(null);

  // Remover referência ao file input - agora usando API

  const getFieldValidationErrors = (row: ParsedData): { [key: string]: boolean } => {
    const fieldErrors: { [key: string]: boolean } = {};

    // Verificar campos obrigatórios
    if (!row.modeloId) fieldErrors.modeloId = true;
    if (!row.numero_serie) fieldErrors.numero_serie = true;
    if (!row.ativo) fieldErrors.ativo = true;
    if (!row.modalidade) fieldErrors.modalidade = true;

    return fieldErrors;
  };

  const parseApiData = (equipamentosData: any[]): ParsedData[] => {
    try {
      const data: ParsedData[] = [];

      equipamentosData.forEach((equipamento) => {
        const equipamentoData: ParsedData = {
          modeloId: equipamento.modeloId,
          numero_serie: equipamento.numero_serie,
          mac_address: equipamento.mac_address,
          gpon_sn: equipamento.gpon_sn,
          modalidade: equipamento.modalidade,
          cto_nome: equipamento.cto_nome,
          caixa_port_number: equipamento.caixa_port_number,
          circuito_id: equipamento.circuito_id,
          provision_status: equipamento.provision_status,
          olt_desc_name: equipamento.olt_desc_name,
          status_uso: equipamento.status_uso,
          createdAt: equipamento.createdAt,
          usuario_pppoe: equipamento.usuario_pppoe,
          ativo: equipamento.ativo,
          valid: true,
          duplicate: false,
          validationErrors: []
        };

        // Validações básicas
        let isValid = true;
        let validationErrors: string[] = [];

        if (!equipamentoData.modeloId) {
          isValid = false;
          validationErrors.push('Modelo ID obrigatório');
        }

        if (!equipamentoData.numero_serie) {
          isValid = false;
          validationErrors.push('Número de série obrigatório');
        }

        if (!equipamentoData.ativo) {
          isValid = false;
          validationErrors.push('Ativo obrigatório');
        }

        if (!equipamentoData.modalidade) {
          isValid = false;
          validationErrors.push('Modalidade obrigatória');
        }

        equipamentoData.valid = isValid;
        equipamentoData.validationErrors = validationErrors;

        data.push(equipamentoData);
      });

      return data;
    } catch (error) {
      console.error('Erro ao processar dados da API:', error);
      throw new Error('Erro ao processar dados da API');
    }
  };



  const handleApiUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiUrl(event.target.value);
    setError(null);
  };

  const handleApiTokenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiToken(event.target.value);
    setError(null);
  };

  const handleFetchData = async () => {
    if (!apiUrl.trim()) {
      setError('URL da API é obrigatória');
      return;
    }

    if (!apiToken.trim()) {
      setError('Token da API é obrigatório');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fazer requisição para a API externa
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
      }

      const apiData = await response.json();

      // Verificar se a resposta contém dados de planos
      let equipamentosData: any[];

      if (Array.isArray(apiData)) {
        equipamentosData = apiData;
      } else if (apiData && typeof apiData === 'object') {
        // Verificar se existe a propriedade 'data' (formato padrão da API)
        if (apiData.data && Array.isArray(apiData.data)) {
          equipamentosData = apiData.data;
          console.log(`Array de equipamentos encontrado na propriedade: data (${equipamentosData.length} equipamentos)`);
        } else {
          throw new Error('Propriedade "data" não encontrada na resposta da API');
        }
      } else {
        throw new Error('Formato de resposta inválido da API');
      }

      if (!Array.isArray(equipamentosData) || equipamentosData.length === 0) {
        throw new Error('Nenhum equipamento encontrado na resposta da API');
      }

      // Processar dados da API
      const parsedData = parseApiData(equipamentosData);

      // Detectar duplicados (baseado no nome do plano)
      const duplicateNames = new Set();
      const finalData = parsedData.map(item => {
        const numero_serie = item.numero_serie?.trim();
        if (numero_serie && duplicateNames.has(numero_serie)) {
          return { ...item, duplicate: true };
        }
        if (numero_serie) {
          duplicateNames.add(numero_serie);
        }
        return item;
      });

      // Ordenar dados: primeiro válidos não-duplicados, depois inválidos, por último duplicados
      const sortedData = finalData.sort((a, b) => {
        // Primeiro critério: válidos não-duplicados primeiro
        if (a.valid && !a.duplicate && (!b.valid || b.duplicate)) return -1;
        if (!a.valid || a.duplicate) {
          if (b.valid && !b.duplicate) return 1;
        }

        // Segundo critério: inválidos antes de duplicados
        if (!a.valid && b.duplicate) return -1;
        if (a.duplicate && !b.valid) return 1;

        // Terceiro critério: ordenar por nome
        return (a.numero_serie || '').localeCompare(b.numero_serie || '');
      });

      setParsedData(sortedData);
      // Selecionar por padrão os válidos e não duplicados
      const defaultSelected = new Set<number>();
      sortedData.forEach((item, idx) => {
        if (item.valid && !item.duplicate) defaultSelected.add(idx);
      });
      setSelectedRows(defaultSelected);
      setImportStats({
        total: finalData.length,
        valid: finalData.filter(item => item.valid && !item.duplicate).length,
        invalid: finalData.filter(item => !item.valid).length,
        duplicates: finalData.filter(item => item.duplicate).length
      });

      setActiveStep(1);
      toast.success(`Dados obtidos com sucesso! ${finalData.length} equipamentos encontrados.`);
    } catch (err: any) {
      console.error('Erro ao buscar dados da API:', err);
      setError(err.message || 'Erro ao conectar com a API. Verifique a URL e o token.');
    } finally {
      setLoading(false);
    }
  };

  // Funções de edição removidas - interface simplificada

  const handleStartImport = async () => {
    setImporting(true);
    setImportProgress({});
    setImportCompleted(false);

    try {
      // Filtrar apenas dados válidos, não duplicados e selecionados
      const validData = parsedData.filter((item, index) => item.valid && !item.duplicate && selectedRows.has(index));

      if (validData.length === 0) {
        throw new Error('Nenhum equipamento válido para importar');
      }

      if (!selectedEscritorio) {
        throw new Error('Selecione um escritório para associar aos equipamentos');
      }

      let successCount = 0;
      let errorCount = 0;

      // Processar cada equipamento individualmente
      for (let i = 0; i < validData.length; i++) {
        const equipamento = validData[i];
        const originalIndex = parsedData.findIndex(p => p === equipamento);

        // Atualizar status para "importando"
        setImportProgress(prev => ({
          ...prev,
          [originalIndex]: {
            nome: equipamento.modeloId,
            status: 'processando'
          }
        }));

        try {
          // Usar o serviço de importação
          const result = await importService.importEquipamentos({
            tableData: [equipamento],
            escritorio_id: selectedEscritorio
          });

          if (result.success && result.data.summary.success > 0) {
            // Sucesso
            setImportProgress(prev => ({
              ...prev,
              [originalIndex]: {
                nome: equipamento.modeloId,
                status: 'sucesso'
              }
            }));
            successCount++;
          } else {
            // Erro retornado pela API
            const errorMsg = result.data.results?.[0]?.erro || 'Erro desconhecido';
            setImportProgress(prev => ({
              ...prev,
              [originalIndex]: {
                nome: equipamento.modeloId,
                status: 'erro',
                erro: errorMsg
              }
            }));
            errorCount++;
          }
        } catch (err: any) {
          // Erro de requisição
          setImportProgress(prev => ({
            ...prev,
            [originalIndex]: {
              nome: equipamento.modeloId,
              status: 'erro',
              erro: err.message
            }
          }));
          errorCount++;
        }

        // Pequeno delay entre requisições para não sobrecarregar
        if (i < validData.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      setImportCompleted(true);

      // Mostrar resultado final
      if (successCount > 0) {
        toast.success(`${successCount} equipamentos importados com sucesso!`);
      }

      if (errorCount > 0) {
        toast.warning(`${errorCount} equipamentos não puderam ser importados. Verifique os detalhes na tabela.`);
      }

      // Se todos foram importados com sucesso, chamar onSuccess
      if (errorCount === 0) {
        setTimeout(() => onSuccess(), 2000);
      }

    } catch (err: any) {
      console.error('Erro ao importar equipamentos:', err);
      setError(err.message || 'Erro ao importar equipamentos. Tente novamente.');
      toast.error(err.message || 'Erro ao importar equipamentos');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setApiUrl('');
    setApiToken('');
    setParsedData([]);
    setError(null);
    setLoading(false);
    // setEditingData removido
    setImporting(false);
    setImportProgress({});
    setImportCompleted(false);
    setSelectedRows(new Set());
    setSelectedEscritorio(null);
    onClose();
  };

  const renderUploadStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Conecte-se à API externa para importar dados de equipamentos
      </Typography>

      <Card sx={{ mt: 3, p: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <CloudUpload sx={{ fontSize: '4rem', color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Configuração da API
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Informe a URL e token de acesso da API externa
              </Typography>
            </Box>

            <TextField
              label="URL da API"
              placeholder="https://api.exemplo.com/equipamentos"
              value={apiUrl}
              onChange={handleApiUrlChange}
              fullWidth
              variant="outlined"
              helperText="URL completa da API que retorna dados de equipamentos"
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <CloudUpload color="action" />
                  </Box>
                )
              }}
            />

            <TextField
              label="Token de Acesso"
              placeholder="seu_token_de_acesso_aqui"
              value={apiToken}
              onChange={handleApiTokenChange}
              fullWidth
              variant="outlined"
              type="password"
              helperText="Token Bearer para autenticação na API"
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <Description color="action" />
                  </Box>
                )
              }}
            />

            {apiUrl && apiToken && (
              <Box sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'success.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.main'
              }}>
                <Typography variant="body2" color="success.dark">
                  ✓ Configuração da API preenchida. Clique em "Buscar Dados" para continuar.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const isRowSelectable = (row: ParsedData) => row.valid && !row.duplicate;

  const totalSelectable = useMemo(() => parsedData.filter(isRowSelectable).length, [parsedData]);
  const selectedCount = useMemo(() => Array.from(selectedRows).length, [selectedRows]);
  const allSelected = totalSelectable > 0 && selectedCount === totalSelectable;
  const someSelected = selectedCount > 0 && selectedCount < totalSelectable;

  const handleSelectRow = (rowIndex: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === parsedData.filter(isRowSelectable).length) {
      setSelectedRows(new Set());
    } else {
      const newSet = new Set<number>();
      parsedData.forEach((row, idx) => {
        if (isRowSelectable(row)) newSet.add(idx);
      });
      setSelectedRows(newSet);
    }
  };

  const renderAnalysisStep = () => (
    <Box>
      {/* Seleção de escritório e resumo */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <EscritorioSelect
                label="Escritório para associar aos equipamentos"
                value={selectedEscritorio || null}
                onChange={(val) => {
                  if (typeof val === 'number') setSelectedEscritorio(val);
                  else setSelectedEscritorio(null);
                }}
                multiple={false}
                required
                allowClear
                size="small"
              />
              {!selectedEscritorio && (
                <Typography variant="caption" color="error">Selecione um escritório</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Chip label={`Selecionados: ${selectedCount}`} color={selectedCount > 0 ? 'primary' : 'default'} size="small" />
                <Chip label={`Válidos: ${importStats.valid}`} color="success" size="small" />
                <Chip label={`Duplicados: ${importStats.duplicates}`} color="warning" size="small" />
                <Chip label={`Inválidos: ${importStats.invalid}`} color="error" size="small" />
                <Chip label={`Total: ${importStats.total}`} size="small" />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Contador de seleção */}
      {selectedRows.size > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Alert severity="info" sx={{ flex: 1 }}>
            <strong>{selectedRows.size}</strong> {selectedRows.size === 1 ? 'registro selecionado' : 'registros selecionados'}
          </Alert>
          <Button
            onClick={() => setSelectedRows(new Set())}
            size="small"
            sx={{ ml: 2 }}
            variant="outlined"
          >
            Limpar Seleção
          </Button>
        </Box>
      )}

      {/* DataGrid com paginação e checkbox customizado */}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={parsedData.map((row, index) => ({ ...row, rowIndex: index }))}
          getRowId={(row) => row.rowIndex}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          columns={[
            {
              field: 'checkbox',
              headerName: '',
              width: 50,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderHeader: () => (
                <IconButton
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ padding: 0 }}
                >
                  {selectedRows.size === 0 ? (
                    <CheckBoxOutlineBlank fontSize="small" />
                  ) : selectedRows.size === parsedData.filter(isRowSelectable).length ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <IndeterminateCheckBox fontSize="small" color="primary" />
                  )}
                </IconButton>
              ),
              renderCell: (params) => (
                <IconButton
                  size="small"
                  onClick={() => handleSelectRow(params.row.rowIndex)}
                  disabled={!isRowSelectable(params.row)}
                  sx={{ padding: 0 }}
                >
                  {selectedRows.has(params.row.rowIndex) ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <CheckBoxOutlineBlank fontSize="small" />
                  )}
                </IconButton>
              ),
            },
            {
              field: 'status',
              headerName: 'Status',
              width: 150,
              renderCell: (params) => {
                const row = params.row;
                const index = params.row.rowIndex;
                const progress = importProgress[index];

                // Se está importando ou há progresso, mostrar status de importação
                if (importing || progress) {
                  if (progress?.status === 'processando') {
                    return (
                      <Chip
                        icon={<Sync className="animate-spin" />}
                        label="Importando..."
                        color="info"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else if (progress?.status === 'sucesso') {
                    return (
                      <Chip
                        icon={<CheckCircle />}
                        label="Importado"
                        color="success"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else if (progress?.status === 'erro') {
                    return (
                      <Chip
                        icon={<ErrorIcon />}
                        label="Erro"
                        color="error"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                        title={progress.erro}
                      />
                    );
                  } else if (importing && row.valid && !row.duplicate) {
                    return (
                      <Chip
                        icon={<Schedule />}
                        label="Aguardando"
                        color="default"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  }
                }

                // Status padrão de validação
                if (row.valid) {
                  if (row.duplicate) {
                    return (
                      <Chip
                        icon={<Warning />}
                        label="Duplicado"
                        color="warning"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else {
                    return (
                      <Chip
                        icon={<CheckCircle />}
                        label="Válido"
                        color="success"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  }
                } else {
                  return (
                    <Chip
                      icon={<ErrorIcon />}
                      label="Inválido"
                      color="error"
                      size="small"
                      sx={{ height: '24px', fontSize: '0.75rem' }}
                    />
                  );
                }
              }
            },
            {
              field: 'erro',
              headerName: 'Erro',
              width: 250,
              renderCell: (params) => {
                const index = params.row.rowIndex;
                const progress = importProgress[index];
                const validationErrors = params.row.validationErrors;

                if (progress?.erro) {
                  return (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                      {progress.erro}
                    </Typography>
                  );
                } else if (validationErrors && validationErrors.length > 0) {
                  return (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                      {validationErrors.join(', ')}
                    </Typography>
                  );
                }
                return '-';
              }
            },
            ...(parsedData.length > 0
              ? Object.keys(parsedData[0])
                .filter(key => !['valid', 'duplicate', 'validationErrors', 'isEditing', 'rowIndex'].includes(key))
                .map(key => ({
                  field: key,
                  headerName: key.replace(/_/g, ' ').toUpperCase(),
                  width: 150,
                  renderCell: (params: any) => {
                    const row = params.row;
                    const fieldErrors = getFieldValidationErrors(row);
                    const hasError = fieldErrors[key];

                    return (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          border: hasError ? '1px dashed #f44336' : 'none',
                          backgroundColor: hasError ? '#ffebee' : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {String(params.value || '')}
                        </Typography>
                      </Box>
                    );
                  }
                }))
              : []
            )
          ]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100, page: 0 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          density="compact"
          sx={{
            '& .MuiDataGrid-cell': {
              padding: '4px 8px',
              fontSize: '0.75rem',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.75rem',
              fontWeight: 'bold',
            },
          }}
        />
      </Box>
    </Box>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderUploadStep();
      case 1:
        return renderAnalysisStep();
      default:
        return null;
    }
  };

  const getActionButtons = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            variant="contained"
            onClick={handleFetchData}
            disabled={!apiUrl || !apiToken || loading}
            startIcon={<CloudUpload />}
          >
            {loading ? 'Buscando Dados...' : 'Buscar Dados'}
          </Button>
        );
      case 1:
        const selectedValidCount = Array.from(selectedRows).filter(index =>
          parsedData[index]?.valid && !parsedData[index]?.duplicate
        ).length;

        return (
          <>
            <Button
              onClick={() => setActiveStep(0)}
              disabled={importing}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              onClick={handleStartImport}
              disabled={selectedValidCount === 0 || importing || !selectedEscritorio}
              startIcon={importing ? <Sync className="animate-spin" /> : <CloudUpload />}
              color="success"
            >
              {importing
                ? `Importando ${selectedValidCount} equipamento(s)...`
                : importCompleted
                  ? 'Importação Concluída'
                  : selectedRows.size === 0
                    ? 'Selecione equipamentos para importar'
                    : `Importar ${selectedValidCount} equipamento${selectedValidCount !== 1 ? 's' : ''} selecionado${selectedValidCount !== 1 ? 's' : ''}`
              }
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Importar Equipamentos via API MKAuth
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {importing && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Importando equipamentos...
            </Typography>
          </Box>
        )}

        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .animate-spin {
              animation: spin 1s linear infinite;
            }
          `}
        </style>

        {getStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        {getActionButtons()}
      </DialogActions>
    </Dialog>
  );
};

export default ImportSysupequipamentosModal;